// Currency conversion and display. Rates arrive per USD from the server, which also says how many
// minor units each currency has (JPY 0, BDT 2, KWD 3) — never assume two decimals.

/** Converts through USD: `perUsd` values are units of that currency per 1 USD. */
export function convert(amount: number, fromPerUsd: number, toPerUsd: number): number {
  if (!(fromPerUsd > 0) || !(toPerUsd > 0)) return NaN;
  return (amount * toPerUsd) / fromPerUsd;
}

function group(intPart: string): string {
  let out = '';
  for (let i = 0; i < intPart.length; i++) {
    if (i > 0 && (intPart.length - i) % 3 === 0) out += ',';
    out += intPart[i];
  }
  return out;
}

/** "1,234,567" for JPY (0 minor units), "1,234.50" for BDT (2). */
export function formatAmount(amount: number, minorUnits: number): string {
  if (!isFinite(amount)) return '—';
  const neg = amount < 0;
  const fixed = Math.abs(amount).toFixed(Math.max(0, minorUnits));
  const dot = fixed.indexOf('.');
  const intPart = dot >= 0 ? fixed.slice(0, dot) : fixed;
  const frac = dot >= 0 ? fixed.slice(dot) : '';
  return `${neg ? '-' : ''}${group(intPart)}${frac}`;
}

/** A unit rate ("1 JPY = 0.7834 BDT"): enough digits to be useful whatever the size. */
export function formatRate(rate: number): string {
  if (!isFinite(rate) || rate <= 0) return '—';
  if (rate >= 1000) return formatAmount(rate, 0);
  if (rate >= 100) return rate.toFixed(2);
  if (rate >= 1) return rate.toFixed(4);
  // Small rates: four significant digits (0.006443).
  const digits = Math.min(10, 3 - Math.floor(Math.log10(rate)));
  return rate.toFixed(digits);
}

/** Parses a typed amount, tolerating thousands separators; NaN when it isn't a positive number. */
export function parseAmount(text: string): number {
  const n = Number(text.replace(/,/g, '').trim());
  return text.trim() !== '' && isFinite(n) && n > 0 ? n : NaN;
}
